import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  useTheme,
} from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { YearlyData } from "../Charts/hooks/useDividendAnalysis";

interface DividendGraphProps {
  yearlyData: YearlyData[];
}

const DividendGraph: React.FC<DividendGraphProps> = ({ yearlyData }) => {
  const theme = useTheme();
  const currencySymbol = "$";

  const chartData = useMemo(() => {
    return yearlyData
      .sort((a, b) => a.year - b.year)
      .map(data => ({
        year: data.year.toString(),
        income: data.totalPayments, // Combined dividends + interest
      }));
  }, [yearlyData]);

  const maxValue = useMemo(() => {
    return Math.max(...chartData.map(d => d.income)) * 1.1; // Add 10% padding
  }, [chartData]);

  return (
    <Card
      elevation={0}
      sx={{
        background: (theme) =>
          theme.palette.mode === "dark"
            ? "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)"
            : "linear-gradient(135deg, #fff 0%, #f8f9fa 100%)",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.05)",
        borderRadius: 3,
        mb: 4,
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Box mb={3}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              fontSize: "1.5rem",
              mb: 1,
              color: (theme) =>
                theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
            }}
          >
            Income History
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a1a1a6" : "#86868b",
              fontSize: "0.875rem",
            }}
          >
            Combined dividend and interest income over time
          </Typography>
        </Box>

        <Box sx={{ height: 400 }}>
          <ResponsiveBar
            data={chartData}
            keys={['income']}
            indexBy="year"
            margin={{ top: 50, right: 60, bottom: 80, left: 80 }}
            padding={0.3}
            valueScale={{ type: 'linear', min: 0, max: maxValue }}
            colors={['#22c55e']}
            colorBy="id"
            borderRadius={4}
            borderWidth={0}
            enableLabel={false}
            legends={[]}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: 'Year',
              legendPosition: 'middle',
              legendOffset: 32,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 5,
              tickRotation: 0,
              legend: `Amount (${currencySymbol})`,
              legendPosition: 'middle',
              legendOffset: -60,
              format: value => `${currencySymbol}${value.toLocaleString()}`,
            }}
            theme={{
              background: "transparent",
              text: {
                fontSize: 14,
                fill: theme.palette.mode === "dark" ? "#ffffff" : "#1d1d1f",
              },
              axis: {
                domain: {
                  line: {
                    stroke: theme.palette.mode === "dark" ? "#374151" : "#e5e7eb",
                    strokeWidth: 1,
                  },
                },
                legend: {
                  text: {
                    fontSize: 14,
                    fill: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  },
                },
                ticks: {
                  line: {
                    stroke: theme.palette.mode === "dark" ? "#374151" : "#e5e7eb",
                    strokeWidth: 1,
                  },
                  text: {
                    fontSize: 13,
                    fill: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                  },
                },
              },
              grid: {
                line: {
                  stroke: theme.palette.mode === "dark" ? "#374151" : "#f3f4f6",
                  strokeWidth: 1,
                },
              },
              legends: {
                text: {
                  fontSize: 12,
                  fill: theme.palette.mode === "dark" ? "#9ca3af" : "#6b7280",
                },
              },
            }}
            tooltip={({ value, color, indexValue }) => (
              <Box
                sx={{
                  background: theme.palette.mode === "dark" ? "#1f2937" : "#ffffff",
                  border: theme.palette.mode === "dark"
                    ? "1px solid #374151"
                    : "1px solid #e5e7eb",
                  borderRadius: 1,
                  padding: 2,
                  boxShadow: theme.shadows[4],
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {indexValue}
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      backgroundColor: color,
                      borderRadius: 1,
                    }}
                  />
                  <Typography variant="body2">
                    Income: {currencySymbol}{value.toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            )}
            animate={true}
            motionConfig="gentle"
          />
        </Box>

        {/* Summary Stats */}
        <Box
          display="flex"
          justifyContent="center"
          gap={4}
          mt={3}
          pt={3}
          sx={{
            borderTop: (theme) =>
              theme.palette.mode === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <Box textAlign="center">
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#22c55e" }}>
              {currencySymbol}{yearlyData.reduce((sum, data) => sum + data.totalDividends, 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Dividends
            </Typography>
          </Box>
          
          <Box textAlign="center">
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#3b82f6" }}>
              {currencySymbol}{yearlyData.reduce((sum, data) => sum + data.totalInterest, 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Interest
            </Typography>
          </Box>
          
          <Box textAlign="center">
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currencySymbol}{yearlyData.reduce((sum, data) => sum + data.totalPayments, 0).toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total Income
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DividendGraph;